import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  fetchPrintletOrder,
  retryPrintletOrder,
  refreshPrintletOrder,
  verifyPrintletCod,
  payPrintletWallet,
  cancelPrintletOrder,
} from "../../api/PrintletAPIs";
import { canMutateOrders } from "../../utils/roles";
import { useAuth } from "../../utils/auth";
import {
  ArrowRight,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
  Wallet,
  XCircle,
  Package,
  Truck,
  User,
  Phone,
  DollarSign,
  AlertTriangle,
  Clock,
  Activity,
  ExternalLink,
  Inbox,
} from "lucide-react";

const STATUS_CONFIG = {
  Preparing:           { label: "قيد التجهيز",       color: "bg-slate-100 text-slate-700 border-slate-200",   dot: "bg-slate-400" },
  ArtworkProcessing:   { label: "معالجة التصميم",    color: "bg-blue-50 text-blue-700 border-blue-200",       dot: "bg-blue-500" },
  ValidationFailed:    { label: "فشل التحقق",        color: "bg-rose-50 text-rose-700 border-rose-200",       dot: "bg-rose-500" },
  Quoted:              { label: "تم التسعير",         color: "bg-amber-50 text-amber-700 border-amber-200",    dot: "bg-amber-500" },
  PendingVerification: { label: "بانتظار التحقق",    color: "bg-orange-50 text-orange-700 border-orange-200", dot: "bg-orange-500" },
  PaymentRequired:     { label: "مطلوب دفع",          color: "bg-red-50 text-red-700 border-red-200",          dot: "bg-red-500" },
  Accepted:            { label: "مقبول",              color: "bg-teal-50 text-teal-700 border-teal-200",       dot: "bg-teal-500" },
  InProduction:        { label: "قيد الإنتاج",        color: "bg-indigo-50 text-indigo-700 border-indigo-200", dot: "bg-indigo-500" },
  Shipped:             { label: "تم الشحن",           color: "bg-violet-50 text-violet-700 border-violet-200", dot: "bg-violet-500" },
  Delivered:           { label: "تم التسليم",         color: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  Failed:              { label: "فشل",                color: "bg-rose-50 text-rose-700 border-rose-200",       dot: "bg-rose-500" },
  Cancelled:           { label: "ملغي",               color: "bg-slate-100 text-slate-500 border-slate-200",   dot: "bg-slate-400" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || {
    label: status || "—",
    color: "bg-slate-100 text-slate-600 border-slate-200",
    dot: "bg-slate-400",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${cfg.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function InfoCard({ icon: Icon, label, children, accent = "slate" }) {
  const accents = {
    slate:   "bg-slate-50 border-slate-200",
    indigo:  "bg-indigo-50 border-indigo-200",
    amber:   "bg-amber-50 border-amber-200",
    emerald: "bg-emerald-50 border-emerald-200",
    violet:  "bg-violet-50 border-violet-200",
  };
  const iconColors = {
    slate:   "text-slate-500",
    indigo:  "text-indigo-600",
    amber:   "text-amber-600",
    emerald: "text-emerald-600",
    violet:  "text-violet-600",
  };
  return (
    <div className={`rounded-xl border p-3 ${accents[accent]}`}>
      <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1.5">
        <Icon size={13} className={iconColors[accent]} />
        {label}
      </div>
      <div className="text-sm font-medium text-slate-800">{children}</div>
    </div>
  );
}

function ActionButton({ icon: Icon, onClick, children, variant = "default", disabled }) {
  const variants = {
    default: "border border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:text-indigo-600",
    primary: "bg-indigo-600 text-white hover:bg-indigo-700",
    danger: "border border-rose-200 bg-white text-rose-600 hover:bg-rose-50",
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition disabled:opacity-50 ${variants[variant]}`}
    >
      <Icon size={15} />
      {children}
    </button>
  );
}

export default function PrintletOrderDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canMutate = canMutateOrders(authState.role);
  const { data, refetch, isLoading, isFetching } = useQuery({
    queryKey: ["printletOrder", id],
    queryFn: () => fetchPrintletOrder(id),
  });

  const [busy, setBusy] = React.useState(null);

  const run = async (key, fn, success) => {
    setBusy(key);
    try {
      await fn();
      toast.success(success);
      refetch();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 h-40 animate-pulse" />
        <div className="bg-white rounded-2xl border border-slate-200 p-5 h-32 animate-pulse" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
        <div className="p-4 rounded-full bg-slate-100 mb-3 inline-flex">
          <Inbox size={32} className="text-slate-400" />
        </div>
        <p className="text-slate-500">الطلب غير موجود</p>
        <button
          onClick={() => navigate("/printlet/orders")}
          className="mt-4 text-indigo-600 hover:text-indigo-700 text-sm"
        >
          رجوع للقائمة
        </button>
      </div>
    );
  }

  const o = data.order || {};
  const items = data.items || [];
  const timeline = data.timeline || [];

  return (
    <div className="space-y-4">
      <ToastContainer position="top-center" />

      {/* Back */}
      <button
        onClick={() => navigate("/printlet/orders")}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition"
      >
        <ArrowRight size={16} />
        رجوع للقائمة
      </button>

      {/* Header Card */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div className="flex items-start gap-3">
            <div className="p-3 rounded-2xl bg-indigo-50 text-indigo-600 border border-indigo-100">
              <Package size={22} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                طلب جراباتكم <span className="text-indigo-600">#{o.girabatOrderId}</span>
              </h2>
              <p className="text-sm text-slate-500 mt-1 font-mono">
                Printlet: {o.printletOrderId || "لم يُنشأ بعد"}
              </p>
            </div>
          </div>
          <StatusBadge status={o.status} />
        </div>

        {/* Actions */}
        {canMutate && (
          <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-100">
            <ActionButton
              icon={RefreshCw}
              onClick={() => run("refresh", () => refreshPrintletOrder(id), "تم التحديث")}
              disabled={busy === "refresh" || isFetching}
            >
              تحديث
            </ActionButton>
            <ActionButton
              icon={RotateCcw}
              onClick={() => run("retry", () => retryPrintletOrder(id), "تمت إعادة المحاولة")}
              disabled={busy === "retry"}
            >
              إعادة محاولة
            </ActionButton>
            <ActionButton
              icon={ShieldCheck}
              onClick={() => run("cod", () => verifyPrintletCod(id, true), "تم تأكيد COD")}
              disabled={busy === "cod"}
            >
              تأكيد COD
            </ActionButton>
            <ActionButton
              icon={Wallet}
              variant="primary"
              onClick={() => run("pay", () => payPrintletWallet(id), "تم الدفع من المحفظة")}
              disabled={busy === "pay"}
            >
              دفع من المحفظة
            </ActionButton>
            <ActionButton
              icon={XCircle}
              variant="danger"
              onClick={() => {
                if (window.confirm("إلغاء طلب Printlet؟")) {
                  run("cancel", () => cancelPrintletOrder(id), "تم طلب الإلغاء");
                }
              }}
              disabled={busy === "cancel"}
            >
              إلغاء
            </ActionButton>
          </div>
        )}
      </div>

      {/* Error banner */}
      {data.lastError && (
        <div className="flex items-start gap-3 bg-rose-50 border border-rose-200 rounded-2xl p-4">
          <AlertTriangle size={18} className="text-rose-600 shrink-0 mt-0.5" />
          <div className="text-sm text-rose-900">
            <b>آخر خطأ:</b> {data.lastError}
          </div>
        </div>
      )}

      {/* Info grid */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="font-bold text-slate-800 mb-4">معلومات الطلب</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          <InfoCard icon={Package} label="حالة جراباتكم" accent="slate">
            {o.girabatStatus || "—"}
          </InfoCard>
          <InfoCard icon={Activity} label="حالة Printlet" accent="indigo">
            {o.status}
            {o.printletStatus && (
              <span className="text-slate-500 text-xs mr-1">({o.printletStatus})</span>
            )}
          </InfoCard>
          <InfoCard icon={DollarSign} label="الدفع" accent="emerald">
            {data.paymentStatus || "—"}
          </InfoCard>
          <InfoCard icon={User} label="العميل" accent="slate">
            <div>{o.customerName || "—"}</div>
            {o.phone && (
              <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5" dir="ltr">
                <Phone size={11} /> {o.phone}
              </div>
            )}
          </InfoCard>
          <InfoCard icon={Truck} label="التتبع" accent="violet">
            {o.carrier || o.trackingNumber ? (
              <>
                <div>{o.carrier || "—"}</div>
                {o.trackingNumber && (
                  <div className="font-mono text-xs mt-0.5" dir="ltr">
                    {o.trackingNumber}
                  </div>
                )}
                {o.trackingUrl && (
                  <a
                    href={o.trackingUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 mt-1"
                  >
                    <ExternalLink size={11} /> فتح التتبع
                  </a>
                )}
              </>
            ) : (
              "—"
            )}
            <p className="text-xs text-slate-400 mt-1.5 font-normal">داخلي للإدارة فقط — غير ظاهر للعميل</p>
          </InfoCard>
          <InfoCard icon={DollarSign} label="COD المُرسل" accent="amber">
            <div>{o.codCollectionAmount != null ? Number(o.codCollectionAmount).toLocaleString() : "—"}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              تكلفة Printlet: {o.printletTotalAmount != null ? Number(o.printletTotalAmount).toLocaleString() : "—"}{" "}
              {o.printletTotalCurrency || ""}
            </div>
          </InfoCard>
        </div>
      </div>

      {/* Items */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-800">البنود</h3>
          <span className="text-xs text-slate-500">{items.length} بند</span>
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center">لا توجد بنود</p>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex items-start justify-between gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-800">
                    {item.designCode} — {item.typeName}
                    <span className="text-slate-500 mr-2 font-normal">× {item.quantity}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs">
                    <span className="px-2 py-0.5 bg-white border border-slate-200 rounded text-slate-600">
                      Artwork: {item.artworkStatus || "—"}
                    </span>
                    <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded">
                      {item.placementPreset}
                    </span>
                  </div>
                </div>
                {item.previewUrl && (
                  <img
                    src={item.previewUrl}
                    alt=""
                    className="h-16 w-16 object-cover rounded-lg border border-slate-200 shrink-0"
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 rounded-xl bg-violet-50 text-violet-600 border border-violet-100">
            <Clock size={16} />
          </div>
          <h3 className="font-bold text-slate-800">الجدول الزمني</h3>
          <span className="mr-auto text-xs text-slate-500">{timeline.length} حدث</span>
        </div>

        {timeline.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center">لا توجد أحداث بعد</p>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute right-[18px] top-2 bottom-2 w-px bg-slate-200" />
            <div className="space-y-4">
              {timeline.map((ev, idx) => (
                <div key={ev.eventId || idx} className="relative flex items-start gap-3">
                  <div className="relative z-10 shrink-0 w-9 h-9 rounded-full bg-white border-2 border-indigo-200 flex items-center justify-center">
                    <Activity size={14} className="text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0 pt-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-bold text-slate-800 text-sm">{ev.eventType}</span>
                      {ev.status && (
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                          {ev.status}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mt-1">
                      {(ev.occurredAtUtc || ev.receivedAtUtc) && (
                        <span className="inline-flex items-center gap-1" dir="ltr">
                          <Clock size={11} /> {ev.occurredAtUtc || ev.receivedAtUtc}
                        </span>
                      )}
                      {ev.source && <span>{ev.source}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}